import React, {
    useContext, useCallback, useMemo, useEffect, useState
} from 'react';
import _ from 'lodash';
import moment from 'moment';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withRouter } from 'react-router-dom';
import { DateUtil } from 'gw-portals-util-js';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { TranslatorContext } from '@jutro/locale';
import {
    ModalNextProvider
} from '@jutro/components';
import { error } from '@jutro/logger';
import { useValidation } from 'gw-portals-validation-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { ViewModelForm, withViewModelService, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import Claim from '../../../models/Claim';
import Contact from '../../../models/Contact';
import metadata from './TypeOfIncidentPage.metadata.json5';
import styles from './TypeOfIncidentPage.module.scss';
import messages from '../../../FNOL.messages';

function FNOLTypeOfIncidentPage(props) {
    const {
        wizardData: claimVM,
        updateWizardData,
        authHeader,
        history: {
            location: { state = {} }
        },
        loggedUserContact
    } = props;
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { FNOLService } = useDependencies('FNOLService');
    const policyType = _.get(claimVM.value, 'policy.policyType', '');
    const lobPolicyType = _.lowerFirst(policyType);
    const lobExtension = claimVM.lobs[lobPolicyType];
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    if (_.isEmpty(claimVM.generalLob)) {
        _.set(claimVM, 'generalLob', lobExtension);
    }
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation
    } = useValidation('FNOLTypeOfIncidentPage');
    const minDate = 1901;
    const maxDate = 2100;

    const initializeVM = useCallback(() => {
        const submittingClaimVM = viewModelService.create(
            new Claim(claimVM.value),
            'cc',
            'com.tdic.edge.capabilities.claim.fnol.dto.FnolDTO_Ext',
            {
                TypeOfIncidentReporter: true
            }
        );
        const newClaimVM = viewModelService.clone(submittingClaimVM);
        updateWizardData(newClaimVM);
        return newClaimVM;
    }, [claimVM, updateWizardData, viewModelService]);

    const accountDetails = useCallback(async () => {
        const newClaimVM = initializeVM();
        const reporter = {
            taxID: null,
            lastName: _.get(loggedUserContact, 'accountContact.lastName', ''),
            firstName: _.get(loggedUserContact, 'accountContact.firstName', ''),
            adanumber: _.get(loggedUserContact, 'accountContact.adaNumber_Ext', '')
        };
        _.set(newClaimVM, 'reporter', reporter);
        _.set(newClaimVM, 'lobs.generalLiability.injuryIncidents[0].injured', new Contact({ subtype: 'New Person' }));
        _.set(newClaimVM.value, 'lobs.generalLiability.injuryIncidents[0].injured.primaryAddress.country', 'US');
        _.set(newClaimVM.value, 'ReportedDate_Ext', new Date());
        if (newClaimVM.typeOfClaim_Ext.value.code !== 'cyberliability') {
            _.set(newClaimVM.value, 'coverageType_Ext', null);
        }
        updateWizardData(newClaimVM);
    }, [initializeVM, loggedUserContact, updateWizardData]);

    useEffect(
        () => {
            accountDetails();
        },
        // Call once when page is mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const getNameValue = useCallback(() => {
        return _.get(loggedUserContact, 'accountContact.displayName', '');
    }, [loggedUserContact]);

    const isValidDate = useCallback((dateValue) => {
        const dateObject = moment(dateValue);
        if (!_.isNull(dateObject)) {
            const dateYear = dateObject.year();
            if (dateYear >= minDate && dateYear <= maxDate) {
                return true;
            }
        }
        return false;
    }, [maxDate, minDate]);

    const requiredField = useCallback(() => {
        const adressValue = _.get(claimVM.value, 'lobs.generalLiability.injuryIncidents[0].injured.primaryAddress.addressLine1');
        const phoneValue = _.get(claimVM.value, 'lobs.generalLiability.injuryIncidents[0].injured.cellNumber');
        const dobValue = _.get(claimVM.value, 'lobs.generalLiability.injuryIncidents[0].injured.dateOfBirth');
        return !_.isEmpty(adressValue) || (!_.isEmpty(phoneValue) && !_.includes(phoneValue, '_'))
        || (!_.isEmpty(dobValue) && isValidDate(dobValue));
    },
    [claimVM.value, isValidDate]);

    const setAvailableLossCauseValue = useMemo(() => {
        const lossDataArray = _.sortBy(claimVM.lossCause.aspects.availableValues.map((key) => {
            return {
                name: translator({
                    id: key.name,
                    defaultMessage: key.name
                }),
                code: key.code
            };
        }));
        return lossDataArray;
    }, [claimVM, translator]);

    const handleSaveClaimData = useCallback(() => {
        setIsLoading(true);
        setLoadingMessage(translator(messages.fnolSaveDraftClaim));
        return FNOLService.saveFNOLDetails(claimVM.value, authHeader)
            .then((response) => {
                const reportedDetails = _.get(claimVM.value, 'reporter');
                claimVM.value = response;
                _.set(claimVM.value, 'reporter', reportedDetails);
                return claimVM;
            })
            .catch((err) => {
                error(`TypeOfIncidentPage: handleSaveClaimData - Unable to handle Save Claim Data ${JSON.stringify(err)}`);
                ModalNextProvider.showAlert({
                    title: messages.fnolSaveDraftErrorTitle,
                    message: messages.fnolSaveDraftErrorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
                return false;
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [FNOLService, authHeader, claimVM, translator]);

    const checkDateWithinRange = useCallback(() => {
        const dateOfNotice = _.get(claimVM.value, 'lobs.generalLiability.dateOfNotice_Ext');
        if (!_.isEmpty(dateOfNotice)) {
            const lossDate = _.get(claimVM.value, 'lossDate');
            const reportedDate = moment().format();
            const dateStatus = DateUtil.compareIgnoreTime(dateOfNotice,
                lossDate);
            const changeDateStatus = DateUtil.compareIgnoreTime(dateOfNotice,
                reportedDate);
            return !((dateStatus === 1 || dateStatus === 0)
            && (changeDateStatus === -1 || changeDateStatus === 0));
        }
        return false;
    }, [claimVM]);

    const writeValue = useCallback(
        (value, path) => {
            const newClaimVM = _.clone(claimVM);
            _.set(newClaimVM, path, value);
            updateWizardData(newClaimVM);
        },
        [claimVM, updateWizardData]
    );

    const onNext = useCallback(() => {
        setIsLoading(true);
        const policyNumber = _.get(claimVM.value, 'policy.policyNumber');
        const isClaimNumberAvailable = _.get(claimVM.value, 'claimNumber');
        if (isClaimNumberAvailable) {
            return handleSaveClaimData();
        }
        setLoadingMessage(translator(messages.generalCreateDraftClaim));
        return FNOLService.getFNOLDetails(policyNumber, claimVM.value, authHeader)
            .then((response) => {
                const reportedDetails = _.get(claimVM.value, 'reporter');
                claimVM.value = new Claim(response);
                _.set(claimVM.value, 'reporter', reportedDetails);
                return claimVM;
            })
            .catch((err) => {
                error(`TypeOfIncidentPage: onNext - Unable to set Loading Message ${JSON.stringify(err)}`);
                ModalNextProvider.showAlert({
                    title: messages.generalCreateDraftErrorTitle,
                    message: messages.generalCreateDraftErrorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
                return false;
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [FNOLService, authHeader, claimVM, handleSaveClaimData, translator]);

    const overrideProps = {
        '@field': {
            showOptional: true,
            labelPosition: 'left'
        },
        lossCause: {
            availableValues: setAvailableLossCauseValue,
            value: _.get(claimVM.value, 'lossCause')
        },
        typeOfIncidentPageLoader: {
            loaded: !isLoading,
            loadingMessage: translator(loadingMessage)
        },
        typeOfIncidentContainer: {
            visible: !isLoading
        },
        describeWhatHappened: {
            visible: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'professionalliability',

            required: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'professionalliability'
        },
        addInjuryElement: {
            visible: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
        },
        claimant: {
            visible: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
        },
        describeIncident: {
            visible: claimVM.value.policy.offeringCode_Ext === 'CyberLiability'
            || claimVM.value.typeOfClaim_Ext === 'cyberliability',

            required: claimVM.value.policy.offeringCode_Ext === 'CyberLiability'
            || claimVM.value.typeOfClaim_Ext === 'cyberliability'
        },
        affectedParty: {
            visible: claimVM.value.policy.offeringCode_Ext === 'CyberLiability'
        },
        affectedPartyType: {
            visible: claimVM.value.policy.offeringCode_Ext === 'CyberLiability'
        },
        incidentName: {
            value: getNameValue()
        },
        blDescribeWhatHappened: {
            visible: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'businessliability',

            required: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'businessliability'

        },
        regulatoryDescribeWhatHappened: {
            visible: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'regulatorylegaldefense',

            required: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'regulatorylegaldefense'
        },
        employmentDescribeWhatHappened: {
            visible: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'employmentpractices',

            required: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'employmentpractices'
        },
        identityDescribeWhatHappened: {
            visible: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'identitytheftrecovery',

            required: claimVM.value.policy.offeringCode_Ext === 'ProfLiabClaimsMade'
            && claimVM.value.typeOfClaim_Ext === 'identitytheftrecovery'
        },
        dateOfNotice: {
            showErrors: checkDateWithinRange(),
            validationMessages: checkDateWithinRange()
                ? [translator(messages.invalidDateOfNotice)] : []
        },
        relationToInsured: {
            value: _.get(claimVM.value, 'lobs.generalLiability.claimReporter_Ext.relationToInsured_Ext')
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrideProps);
        },
        [claimVM, overrideProps]
    );

    const { claimStatus } = state;

    const validateClaimStatus = useCallback(() => {
        return (
            !_.isNil(claimStatus)
            && claimStatus === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'Draft' })
        );
    }, [claimStatus, translator]);

    useEffect(() => {
        registerInitialComponentValidation(validateClaimStatus);
    }, [validateClaimStatus, registerInitialComponentValidation]);

    const isValid = isComponentValid && requiredField();

    return (
        <WizardPage
            disableNext={!isValid}
            skipWhen={initialValidation}
            onNext={onNext}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                resolveValue={readValue}
                onValueChange={writeValue}
            />
        </WizardPage>
    );
}

FNOLTypeOfIncidentPage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(withViewModelService(
    withApplicationPropertyContext(FNOLTypeOfIncidentPage)
)));
